import { useEffect, useState } from "react";
import api, { apiErrorMessage, formatPLN } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import CsvImportModal from "@/components/CsvImportModal";
import { toast } from "sonner";
import { Save, Plus, Upload, X } from "lucide-react";

const NEW_SITE = { name: "", monthly_budget: 10000, auto_approve_limit: 2000, approval_required_for_all_orders: false };

export default function AdminSites() {
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [adding, setAdding] = useState(false);
  const [newSite, setNewSite] = useState(NEW_SITE);
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    const s = await api.get("/sites");
    setSites(s.data);
    const u = await api.get("/users");
    setUsers(u.data);
    const d = {};
    s.data.forEach((x) => { d[x.id] = { ...x }; });
    setDrafts(d);
  };

  useEffect(() => { load(); }, []);

  const save = async (id) => {
    try {
      const patch = drafts[id];
      await api.patch(`/sites/${id}`, {
        name: patch.name,
        monthly_budget: Number(patch.monthly_budget),
        manager_user_id: patch.manager_user_id || null,
        employee_user_id: patch.employee_user_id || null,
        active: patch.active,
      });
      toast.success("Zapisano");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/sites", {
        name: newSite.name,
        company: "NDI",
        monthly_budget: Number(newSite.monthly_budget),
        auto_approve_limit: Number(newSite.auto_approve_limit),
        approval_required_for_all_orders: !!newSite.approval_required_for_all_orders,
      });
      toast.success("Utworzono budowę");
      setAdding(false);
      setNewSite(NEW_SITE);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const managers = users.filter((u) => u.role === "manager");
  const employees = users.filter((u) => u.role === "employee");

  return (
    <div>
      <PageHeader
        testId="admin-sites"
        title="Budowy NDI"
        subtitle={`${sites.length} budów`}
        actions={
          <>
            <button onClick={() => setImportOpen(true)} data-testid="import-sites-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-300 hover:border-[#FF6B1A] hover:text-[#FF6B1A] text-sm font-semibold transition-colors">
              <Upload className="w-4 h-4" /> Importuj CSV
            </button>
            <button onClick={() => setAdding(true)} data-testid="add-site-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-sm font-semibold">
              <Plus className="w-4 h-4" /> Dodaj budowę
            </button>
          </>
        }
      />

      {adding && (
        <form onSubmit={create} className="ol-card p-5 mb-4" data-testid="add-site-form">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Nowa budowa</h3>
            <button type="button" onClick={() => setAdding(false)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Nazwa *</label>
              <input required value={newSite.name} onChange={(e) => setNewSite({ ...newSite, name: e.target.value })} data-testid="new-site-name"
                placeholder="np. S6 Odcinek 4 Goleniów – Nowogard"
                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Budżet miesięczny (PLN) *</label>
              <input required type="number" min="0" step="0.01" value={newSite.monthly_budget}
                onChange={(e) => setNewSite({ ...newSite, monthly_budget: e.target.value })} data-testid="new-site-budget"
                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Limit auto-akceptacji (PLN)</label>
              <input type="number" min="0" step="0.01" value={newSite.auto_approve_limit}
                onChange={(e) => setNewSite({ ...newSite, auto_approve_limit: e.target.value })} data-testid="new-site-limit"
                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
            </div>
            <label className="flex items-start gap-2 cursor-pointer pt-6">
              <input type="checkbox" checked={!!newSite.approval_required_for_all_orders}
                onChange={(e) => setNewSite({ ...newSite, approval_required_for_all_orders: e.target.checked })} data-testid="new-site-required"
                className="mt-0.5 w-4 h-4 accent-[#FF6B1A]" />
              <span className="text-xs text-slate-700">Każde zamówienie wymaga akceptacji kierownika</span>
            </label>
          </div>
          <p className="mt-3 text-xs text-slate-500">💡 Po utworzeniu przypisz pracownika i kierownika edytując budowę poniżej.</p>
          <div className="flex justify-end mt-4 gap-2">
            <button type="button" onClick={() => setAdding(false)} className="px-4 py-2 rounded-md border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50">Anuluj</button>
            <button type="submit" data-testid="create-site-submit"
              className="px-4 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-sm font-semibold">Utwórz budowę</button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {sites.map((s) => {
          const d = drafts[s.id] || s;
          return (
            <div key={s.id} className="ol-card p-5" data-testid={`site-${s.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Nazwa</label>
                  <input value={d.name || ""} onChange={(e) => setDrafts({ ...drafts, [s.id]: { ...d, name: e.target.value } })}
                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Budżet miesięczny</label>
                  <input type="number" value={d.monthly_budget || 0}
                    onChange={(e) => setDrafts({ ...drafts, [s.id]: { ...d, monthly_budget: e.target.value } })}
                    data-testid={`budget-input-${s.id}`}
                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
                  <p className="text-xs text-slate-500 mt-1">obecnie: {formatPLN(s.monthly_budget)}</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Pracownik</label>
                  <select value={d.employee_user_id || ""}
                    onChange={(e) => setDrafts({ ...drafts, [s.id]: { ...d, employee_user_id: e.target.value } })}
                    data-testid={`emp-select-${s.id}`}
                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white">
                    <option value="">— brak —</option>
                    {employees.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} · {u.email}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Kierownik</label>
                  <select value={d.manager_user_id || ""}
                    onChange={(e) => setDrafts({ ...drafts, [s.id]: { ...d, manager_user_id: e.target.value } })}
                    data-testid={`mgr-select-${s.id}`}
                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white">
                    <option value="">— brak —</option>
                    {managers.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} · {u.email}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button onClick={() => save(s.id)} data-testid={`save-site-${s.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-xs font-semibold">
                  <Save className="w-3.5 h-3.5" /> Zapisz
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {importOpen && (
        <CsvImportModal
          endpoint="/import/sites"
          title="Importuj budowy z CSV"
          headers={["name", "monthly_budget", "auto_approve_limit", "approval_required_for_all_orders", "company"]}
          sample={[
            ["S6 Odcinek 4 Goleniów – Nowogard", "15000", "2000", "false", "NDI"],
            ["A1 Etap 1 Tuszyn – Piotrków", "20000", "3000", "true", "NDI"],
          ]}
          templateName="szablon_budowy.csv"
          onClose={() => setImportOpen(false)}
          onDone={load}
        />
      )}
    </div>
  );
}
